import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Toolbar, useTheme } from '@mui/material';
import { AppBar } from '../appBar/appBar';
import { LoginDomainService } from '../../pages/login/loginDomainService';
import { di } from '../../state/inversify.config';
import { NotificationRepository } from '../../state/reduxRepositories';
import { AuthService } from '../../state/helperServices/auth/auth.service';

interface LayoutProps {
  drawerNavigationItems: any;
}
export function Layout({ drawerNavigationItems }: LayoutProps): JSX.Element {
  const notificationRepository = di.get<NotificationRepository>(
    NotificationRepository,
  );
  const loginDomainService = di.get<LoginDomainService>(LoginDomainService);
  const isNetworkConnected = loginDomainService.useNetworkSelector();

  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const { t } = useTranslation();
  const theme = useTheme();
  const { pathname } = useLocation();
  const isHome = pathname.split('/').length <= 2;

  const [open, setOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const drawerWidth = 240;

  const handleDrawerMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerDesktopToggle = () => {
    setOpen(!open);
  };

  const foundUnreadNotificationCount = notificationRepository
    .useResourcesSelector()
    .filter((n) => !n.read);

  const logout = () => {
    if (!isNetworkConnected) {
      // TODO: create a global notification layout that shows up in the same place no matter where you send a notification from. Then remove this alert and send this as a global notification.
      alert(`You have to be online to logout`);
      return;
    }
    loginDomainService.dispatchLogout();
  };

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: pathname,
    });
  }, [pathname]);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: theme.palette.background.default,
      }}
    >
      <AppBar
        open={open}
        mobileOpen={mobileOpen}
        handleDrawerMobileToggle={handleDrawerMobileToggle}
        handleDrawerDesktopToggle={handleDrawerDesktopToggle}
        drawerNavigationItems={drawerNavigationItems}
        menuItems={[
          {
            text: t('LOGOUT'),
            value: 'logout',
            onClick: () => logout(),
          },
        ]}
        loggedInUser={loggedInUser}
        unreadNotificationCount={foundUnreadNotificationCount.length}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            sm: `calc(100% - ${drawerWidth}px)`,
            md: `calc(100% - ${drawerWidth}px)`,
          },
          background: theme.palette.background.default,
        }}
      >
        <Toolbar />
        <Box
          component="main"
          sx={{
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: {
              lg: -30,
            },
            ...(open && {
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
              marginLeft: { sm: 30, md: 30, lg: 30 },
            }),
            mt: 2,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
